import { useEffect, useState } from 'react';
import {
  DurationUnit,
  ProductPrice,
  RentalProductPrice,
  SaleProductPrice,
} from '../../../../clients/quipli-service/pricing/pricing';
import { useAPI } from '../../../../clients/quipli-service/quipli-service-client';
import { User } from '../../../../clients/quipli-service/user/user';
import { usePricingApiV1 } from '../../../../clients/quipli-service/pricing/pricing-api-v1';
import { groupBy, partition, toArray } from 'lodash';
import { OrderType } from '../../../../clients/quipli-service/order/order';
import { displayMoneyFormat } from '../../../../util/util';
import { toast } from '../../../../util/toasts';
import { ApiError } from '../../../../clients/axios-instance';
import { Button } from '../../../../components/form/button';
import { DataTable } from '../../../../components/table/data-table';
import Icon from '../../../../components/icons/icon';
import { UserRentalPriceModal } from '../modals/user-rental-price-modal';
import { UserSalePriceModal } from '../modals/user-sale-price-modal';
import { Product } from '../../../../clients/quipli-service/product/product';
import { DateTime } from 'luxon';
import { useImportExportApi } from '../../../../clients/quipli-service/import-export/import-export-api';
import FileSaver from 'file-saver';

type RentalProductPriceTableRowData = {
  id: number;
  product: string;
  prices: JSX.Element[];
  from: string | undefined;
  to: string | undefined;
  actions: string | JSX.Element;
};

type SaleProductPriceTableRowData = {
  id: string;
  price: string;
  from: string | undefined;
  to: string | undefined;
  actions: string | JSX.Element;
};

export const Pricing = ({ user }: { user: User }) => {
  const { exportCustomerPricing } = useImportExportApi();
  const { listPrices, listPricesByUserId } = usePricingApiV1();
  const { deletePrice, getMultipleConfigurations, getMultipleProductsByIds } = useAPI();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [rentalPricesData, setRentalPricesData] = useState<RentalProductPriceTableRowData[]>([]);
  const [salePricesData, setSalePricesData] = useState<SaleProductPriceTableRowData[]>([]);
  const [currency, setCurrency] = useState<string>('USD');
  const [showRangePriceUnits, setShowRangePriceUnits] = useState<boolean>(false);
  const [selectedPrice, setSelectedPrice] = useState<ProductPrice | undefined>();
  const [selectedProduct, setSelectedProduct] = useState<Product | undefined>();
  const [isPricingModalOpen, setIsPricingModalOpen] = useState<OrderType | undefined>();

  const rentalColumnNames = [
    { key: 'product', label: 'Product' },
    { key: 'prices', label: 'Rental Prices' },
    { key: 'from', label: 'Starts On' },
    { key: 'to', label: 'Expires On' },
    { key: 'actions', label: ' ' },
  ];

  const saleColumnNames = [
    { key: 'product', label: 'Product/Part/Service' },
    { key: 'price', label: 'Sale Price' },
    { key: 'from', label: 'Starts On' },
    { key: 'to', label: 'Expires On' },
    { key: 'actions', label: ' ' },
  ];

  const reloadPrices = async () => {
    setIsLoading(true);

    const userProductPrices = await listPricesByUserId(user.id);
    const productIds = userProductPrices.map((p) => p.productId);
    const productRentalPrices = await listPrices({ productIds, type: OrderType.Rental });
    const products = await getMultipleProductsByIds(productIds);

    const [userRentalPrices, userSalePrices] = partition(
      userProductPrices.filter(
        (p) =>
          p.type === OrderType.Sale ||
          (p.type === OrderType.Rental && p.durationUnit !== DurationUnit.minute && p.durationValue !== 1440),
      ),
      (p) => p.type === OrderType.Rental,
    );

    const rpps = showRangePriceUnits
      ? toArray(
          groupBy(
            (userRentalPrices as RentalProductPrice[]).sort((a, b) => a.durationValue - b.durationValue),
            (p) => p.durationUnit,
          ),
        ).flat()
      : (userRentalPrices as RentalProductPrice[]);

    const rentalProductIds = rpps.map((p) => p.productId);
    const rentalTableData = products
      .filter((p) => rentalProductIds.includes(p.id))
      .sort((a, b) => a.id - b.id)
      .map((product) => {
        return renderRentalProductRow(
          product,
          rpps.filter((p) => p.productId === product.id) as RentalProductPrice[],
          productRentalPrices.filter((p) => p.productId === product.id) as RentalProductPrice[],
        );
      });

    setRentalPricesData(rentalTableData);

    const spd = (userSalePrices as SaleProductPrice[]).map((spp) => {
      return {
        id: spp.id,
        product: products.find((p) => p.id === spp.productId)?.name,
        price: displayMoneyFormat(spp.price / 100, currency),
        from: spp.from
          ? spp.from.toFormat('LLLL d, y')
          : spp.createdAt.toMillis() != 0
          ? spp.createdAt.toFormat('LLLL d, y')
          : undefined,
        to: spp.to && spp.to.toFormat('LLLL d, y'),
        actions: spp.deletedAt ? (
          ''
        ) : (
          <div className="flex justify-end">
            <i
              className="fa-solid fa-pencil mr-3 cursor-pointer"
              onClick={() => {
                setSelectedPrice(spp);
                setIsPricingModalOpen(OrderType.Sale);
              }}
            />
            <i className="fa-solid fa-trash-can cursor-pointer" onClick={() => deactivatePrice(spp.id)} />
          </div>
        ),
      };
    });
    setSalePricesData(spd);

    setIsLoading(false);
  };

  const renderRentalProductRow = (
    product: Product,
    userProductPrices: RentalProductPrice[],
    allProductPrices: RentalProductPrice[],
  ) => {
    const overriddenPriceIds = userProductPrices.map((r) => r.originalPrice?.id).filter((id) => !!id);

    const prices = [
      ...userProductPrices,
      ...(allProductPrices.filter((price) => !overriddenPriceIds.includes(price.id)) as RentalProductPrice[]),
    ].sort((a, b) => a.durationMinutes - b.durationMinutes);

    const priceElements = prices.map((price) => {
      return (
        <span className="mr-3">
          {`${displayMoneyFormat(price.price / 100, currency)} / ${price.durationValue} ${price.durationUnit}`}{' '}
        </span>
      );
    });

    const startDates = userProductPrices.map((price) => price.from).filter((date) => date !== undefined) as DateTime[];
    const minStartsOn = startDates.reduce((min, date) => (date < min ? date : min), startDates[0]);
    const endDates = userProductPrices.map((price) => price.to).filter((date) => date !== undefined) as DateTime[];
    const maxEndsOn = endDates.reduce((max, date) => (date > max ? date : max), endDates[0]);

    return {
      id: product.id,
      product: product.name,
      prices: priceElements,
      from: minStartsOn?.toFormat('LLLL d, y'),
      to: maxEndsOn?.toFormat('LLLL d, y'),
      actions: (
        <div className="flex justify-end">
          <i
            className="fa-solid fa-pencil mr-3 cursor-pointer"
            onClick={() => {
              setSelectedProduct(product);
              setIsPricingModalOpen(OrderType.Rental);
            }}
          />
        </div>
      ),
    };
  };

  const deactivatePrice = async (priceId: string) => {
    await deletePrice(priceId)
      .then(() => {
        toast('Successfully deleted price', 'success');
      })
      .catch((err: ApiError) => {
        const errMsg = err.status === 400 && err.message ? err.message : 'Error deleting price';
        toast(errMsg, 'error');
      });
    return reloadPrices();
  };

  const exportPrices = async () => {
    try {
      const file = await exportCustomerPricing(user.id);

      const exportFileName = `Pricing_${user.name.replaceAll(' ', '_')}_${DateTime.local()
        .toFormat('D')
        .replaceAll('/', '-')}`;
      const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
      const fileExtension = '.xlsx';

      const data = new Blob([file], { type: fileType });
      FileSaver.saveAs(data, `${exportFileName}${fileExtension}`);
    } catch (error) {
      toast('Error exporting prices', 'error');
    }
  };

  useEffect(() => {
    getMultipleConfigurations(['CONF_CURRENCY', 'CONF_SHOW_RANGE_PRICE_UNITS'])
      .then((configs) => {
        const currencyConfig = configs.find((c) => c.variable === 'CONF_CURRENCY');
        setCurrency(currencyConfig?.value || 'USD');
        const showRangePriceUnitsConfig = configs.find((c) => c.variable === 'CONF_SHOW_RANGE_PRICE_UNITS');
        setShowRangePriceUnits(showRangePriceUnitsConfig?.value === '1');
      })
      .catch((err: ApiError) => {
        const errMsg = err.status === 400 && err.message ? err.message : 'Error loading configurations';
        toast(errMsg, 'error');
      });

    reloadPrices();
  }, []);

  return (
    <div className="pricing-tab m-4 font-sans space-y-6">
      <div className="mb-3 flex items-center justify-end">
        <Button className="button-secondary mr-4 " onClick={() => exportPrices()}>
          <span className="flex items-center">
            <Icon icon="Download" size="medium" stroke="var(--quipli-black-800)" />
            Export
          </span>
        </Button>
      </div>

      {/* Rental Prices */}
      <div className="rental-section">
        <div className="mb-3 flex items-center justify-between">
          <span className="ml-4 p-0 text-xl font-light capitalize text-gray-700">Rental Prices</span>
          <Button
            className="button-primary mr-4 "
            onClick={() => {
              setSelectedPrice(undefined);
              setIsPricingModalOpen(OrderType.Rental);
            }}
          >
            <span className="flex items-center">
              <Icon icon="Plus" className="pr-2" stroke="white" />
              Add Rental Price
            </span>
          </Button>
        </div>

        <DataTable
          columnNames={rentalColumnNames}
          className="pricing-table"
          data={rentalPricesData.map((d) => ({ mobileHeader: null, fields: d }))}
          showAlwaysDesktop={true}
          noDataText="No user rental pricing yet"
          isLoading={isLoading}
        />

        <UserRentalPriceModal
          isOpen={isPricingModalOpen === OrderType.Rental}
          user={user}
          currency={currency}
          product={selectedProduct}
          close={async (refresh: boolean = false) => {
            setSelectedPrice(undefined);
            await (refresh ? reloadPrices() : Promise.resolve());
            setIsPricingModalOpen(undefined);
          }}
        />
      </div>

      {/* Sale Prices */}
      <div className="sale-section">
        <div className="mb-3 flex items-center justify-between">
          <span className="ml-4 p-0 text-xl font-light capitalize text-gray-700">Sale Prices</span>
          <Button
            className="button-primary mr-4"
            onClick={() => {
              setSelectedPrice(undefined);
              setIsPricingModalOpen(OrderType.Sale);
            }}
          >
            <span className="flex items-center">
              <Icon icon="Plus" className="pr-2" stroke="white" />
              Add Sale Price
            </span>
          </Button>
        </div>

        <DataTable
          columnNames={saleColumnNames}
          data={salePricesData.map((d) => ({ mobileHeader: null, fields: d }))}
          showAlwaysDesktop={true}
          noDataText="No sale pricing yet"
          isLoading={isLoading}
        />

        <UserSalePriceModal
          isOpen={isPricingModalOpen === OrderType.Sale}
          user={user}
          price={selectedPrice as SaleProductPrice}
          close={async (refresh: boolean = false) => {
            setSelectedPrice(undefined);
            await (refresh ? reloadPrices() : Promise.resolve());
            setIsPricingModalOpen(undefined);
          }}
        />
      </div>
    </div>
  );
};
